/**
 * Check auth hook
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import type { User } from '@/types/user';
import { fetchUser as fetchAclUser } from '@/hooks/acl';

export type AuthResponse = {
    authenticated: boolean;
    message: string;
    success?: boolean;
    access?: {
        roles: string[];
        permissions: string[];
    };
};

interface AuthContextType {
    user: User | null;
    roles: string[];
    permissions: string[];
    isAuthenticated: boolean;
    isLoading: boolean;
    refetchUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
    user: null,
    roles: [],
    permissions: [],
    isAuthenticated: false,
    isLoading: true,
    refetchUser: async () => {},
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [roles, setRoles] = useState<string[]>([]);
    const [permissions, setPermissions] = useState<string[]>([]);
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const checkAuthAndUser = useCallback(async (isBackground?: boolean) => {
        if (!isBackground) {
            setIsLoading(true);
        }
        try {
            const sessionResponse = await fetch(`${process.env.REACT_APP_API_URL}/auth/session`, {
                credentials: 'include',
            });
            const sessionData: AuthResponse = await sessionResponse.json();
            
            if (sessionData.authenticated) {
                const userData = await fetchAclUser();
                
                if (sessionData.access) {
                    setRoles(sessionData.access.roles || []);
                    setPermissions(sessionData.access.permissions || []);
                    userData.roles = sessionData.access.roles || [];
                }

                setUser(userData);
                setIsAuthenticated(true);
            } else {
                setUser(null);
                setRoles([]);
                setPermissions([]);
                setIsAuthenticated(false);
            }
        } catch (error) {
            console.error('Auth error:', error);
            setUser(null);
            setRoles([]);
            setPermissions([]);
            setIsAuthenticated(false);
        } finally {
            if (!isBackground) {
                setIsLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        checkAuthAndUser(false);
    }, [checkAuthAndUser]);

    return (
        <AuthContext.Provider value={{ user, roles, permissions, isAuthenticated, isLoading, refetchUser: checkAuthAndUser }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);

export const fetchUser = async () => {
	try {
		const url = `${process.env.REACT_APP_API_URL}/auth/me`
		const response = await fetch(url, {
			credentials: 'include',
		})
		const data = await response.json()

		if(data.success) {
			return data.user as User
		} else {
			throw new Error(data.message)
		}
	} catch (error) {
		console.log('Error fetching user', error)
		throw new Error('Error fetching user')
	}
}